/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One offer in the comparison list — a compact row on desktop (`xl`+), a
 * two-line card from `sm`, and on a phone the old card's composition with
 * the new card's facts: a header (logo, name, a round arrow to the profile),
 * ONE inset panel whose first row is the net figure (label over it) beside
 * the CTA and whose rows below are the labelled facts, and an action strip
 * (Vergelijk · Details) on its bottom edge — the 2026-10 redesign
 * (docs/plan-vergelijker-resultaten-2026-09-30.md §3; the phone card and the
 * tablet's labelled facts line: docs/plan-vergelijker-telefoonkaart-2026-10-02.md,
 * the panel with the net figure and the CTA since its §10).
 * One markup path; the layout is the `.result-grid` template in src/index.css,
 * shared with the column-header row so the columns always line up.
 *
 * What the collapsed row says, in the same slots on every row (Baymard 2023:
 * a missing attribute reads as "not offered", so nothing appears on some rows
 * only): the provider, the estimated net payout (the default sort key), the
 * commission with its tier, the looptijd, the payout frequency with its timing
 * line, the terms as two values — the cancel deadline over the term, in their
 * tones (src/lib/termsPresentation.ts, plan-voorwaarden-vergelijking-2026-10-07
 * §6.1, composition A) —, the smart-charger mark and the action. The smart
 * cell is the one slot that may stay empty: a required smart charger is the
 * norm and prints nothing (D22); not required is a green mark, unknown a muted
 * "Niet gecontroleerd". Every fact carries an sr-only prefix: there is no
 * header below `xl` and the list is not a table, so each row has to read on
 * its own. Below `xl` each fact also shows its label for the eye — a
 * decorative (aria-hidden) twin of the prefix.
 *
 * What stays behind the Details toggle (plan-voorwaarden-vergelijking-2026-10-07
 * §6.2, composition A, D20): the Voordelen and Aandachtspunten DERIVED from the
 * coded terms (src/lib/termsVerdict.ts — never free text; the redactie's one
 * sourced bullet per side comes last, with its "bron"; the Voordelen open with
 * what the row is best at in its result set, D36: src/lib/relativeBullets.ts)
 * beside the fact TABLE
 * (src/lib/termsPresentation.ts: every question with its value in its tone,
 * when the terms were read and whose words the facts are — no legend, D26), and
 * under them the way to the profile, alone (D27: the row already carries what
 * the foot used to — the "+ … servicekosten" line under the net figure when
 * there are costs; the review score it also named left with the ratings,
 * 2026-10-14; a provider with our form gets its website link before it, muted,
 * since 2026-10-23 — the form stays the row's CTA). The smart-charger
 * question is the table's button for the info modal. Three columns from xl
 * (3/12 · 3/12 · 6/12), bullets | table from md, one column below. On Dutch
 * pages that panel is mounted while collapsed (height 0 + inert), so the baked
 * HTML carries it for crawlers — the rule from the cards.
 *
 * Unknown never reads as bad: a dash in `text-fg-muted` — legible, never red,
 * never amber.
 * SSR-safe: no window, no Date, no storage; the checkbox renders unchecked on
 * the server and the engine only ticks it in an effect.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, m } from 'motion/react';
import {
  AlertCircle,
  ArrowRight,
  Building2,
  ChevronDown,
  ExternalLink,
  ListChecks,
  Users,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/cn';
import { eurDecimals, formatCurrencyEUR } from '../../lib/formatters';
import { fillTemplate } from '../../lib/templateVars.js';
import { providerBackState } from '../../lib/providerBackLink';
import { DURATION_LABEL_KEYS, type CanonicalDuration } from '../../lib/durationOptions';
import { estimateOfferBreakdown } from '../../lib/earnings';
import { rowOfferPricing, shownCommissionPct } from '../../lib/offerPricing.js';
import {
  formatCommissionPct,
  offerBreakdownTitle,
  offerPricingTooltip,
} from '../../lib/offerPricingText';
import { commissionSortValue } from '../../lib/comparerSort';
import {
  COMMISSION_TIER_TEXT_CLASS,
  commissionTierLabelKey,
  getCommissionTier,
} from '../../lib/commissionTier';
import {
  dossierProvenance,
  termsFactTable,
  termsPayoutCell,
  termsRowCell,
} from '../../lib/termsPresentation';
import { termsVerdict } from '../../lib/termsVerdict';
import { relativeBulletItems, type RelativePluspunten } from '../../lib/relativeBullets';
import { ProviderLogo } from '../ProviderLogo';
import { loadSignupModal, preloadOnIntent } from '../lazyModals';
import { DerivedBullets } from '../terms/DerivedBullets';
import { SmartMark } from '../terms/SmartMark';
import { TermsCell } from '../terms/TermsCell';
import { TermsFactTable } from '../terms/TermsFactTable';
import { useTermsText } from '../terms/termsText';
import { TONE_TEXT } from '../terms/toneText';
import type { EREProvider } from '../../types';
import { providerProfilePath } from '../../lib/providerRoutes.js';

export interface ResultRowProps {
  provider: EREProvider;
  /** 0-based rank in the filtered, sorted list (position = index + 1). */
  index: number;
  /** Estimated net payout at the comparison's volume; null when it cannot be estimated. */
  earnings: number | null;
  /** The kWh the comparison calculates with (the panel's minimum-volume check). */
  volume: number;
  /** €/ERE in force — for the breakdown behind the net figure. */
  erePrice: number;
  isTopEarner: boolean;
  isLowestCommission: boolean;
  /**
   * What the row is best at in its result set (D36, src/lib/relativeBullets.ts):
   * the (shared) lowest commission and the top of the active order — pluspunten
   * that lead its Voordelen. Omitted: none.
   */
  relative?: RelativePluspunten;
  /** Only with the audience filter on "Alle": every row would say the same word otherwise. */
  showAudiencePill: boolean;
  /**
   * The row's place in its entrance stagger (0 = first), or null for no
   * entrance. The engine sets it on the first paint and for a batch "Toon meer"
   * reveals, never for a row a sort or a filter merely moves: a moved element
   * restarts its CSS animation. Omitted: the row's rank, the first-paint entrance.
   */
  entranceStep?: number | null;
  expanded: boolean;
  /** Keep the details panel in the DOM while collapsed (Dutch pages). */
  mountDetails: boolean;
  /**
   * The panel's mount generation. The engine bumps it when it restores open
   * rows after mount (a reload onto a remembered list): the panel then mounts
   * afresh in the state it is in — open at its full height at once, as a
   * panel open at a row's own mount always is — instead of animating open
   * under the scroll manager's restore. Omitted: 0.
   */
  detailsEpoch?: number;
  selected: boolean;
  /** False once three offers are selected and this one is not among them. */
  selectable: boolean;
  /** The outbound signup URL (already tagged); null = no CTA (unknown data). */
  ctaHref: string | null;
  /**
   * The provider's HOMEPAGE (already tagged) for the panel foot's secondary
   * "Naar website" of an acceptsSignups row — never the CTA's signup link
   * (Jasper 2026-10-23); null = none (unknown data, no URL, or no form).
   */
  websiteHref: string | null;
  onToggleExpanded: () => void;
  onToggleSelected: () => void;
  /** The outbound anchor was clicked (analytics only — the anchor navigates). */
  onSignupClick: () => void;
  /** The in-site signup form was requested (acceptsSignups rows). */
  onOpenSignup: () => void;
  /**
   * The panel foot's secondary "Naar website" link of an acceptsSignups row was
   * clicked (analytics only — the anchor navigates; 2026-10-23).
   */
  onWebsiteClick: () => void;
  onProfileClick: () => void;
  onSmartChargerInfo: () => void;
  rowRef?: (el: HTMLLIElement | null) => void;
}

const UNKNOWN = '–';

/** The entrance stagger: 45 ms a step, at most 8 steps (index.css `.reveal-rise` runs 0.5 s). */
const ENTRANCE_STAGGER_MS = 45;
const ENTRANCE_MAX_STEPS = 8;
/**
 * When the longest entrance has played: the last step's delay plus the 0.5 s
 * animation, and a frame to spare. The engine drops the class after it.
 */
export const ROW_ENTRANCE_MS = ENTRANCE_MAX_STEPS * ENTRANCE_STAGGER_MS + 500 + 40;

/**
 * A tag on the row's top edge: the two rule-based badges ("Hoogste opbrengst",
 * "Laagste commissie") and, under "Alle", the audience. Absolutely placed, so
 * a row that earns one is exactly as tall as one that does not (Jasper
 * 2026-10-01: the rows are equal in size and format). Centred on the border,
 * inside the list's 12 px gap: 16 px tall with 10 px words on the phone card
 * (8 px stand above it), 14 px with 9 px words from sm (7 px). The background
 * is opaque — it covers the border it sits on.
 */
const TAG_CLASS =
  'inline-flex items-center gap-1 h-4 sm:h-3.5 px-1.5 rounded-full border text-[10px] sm:text-[9px] font-black uppercase tracking-wide leading-none whitespace-nowrap';
const BADGE_TAG_CLASS = cn(TAG_CLASS, 'bg-emerald-700 border-emerald-700 text-white');
/**
 * The emerald of the net figure and its badge. The light theme maps emerald-400
 * to #059669, 3.4:1 on the light card — enough for the cards' 30 px figure (large
 * text), not for this row's 16–18 px one or a 9 px badge (measured 2026-10-01);
 * emerald-700 there is the header's own light-mode idiom.
 */
const EMERALD_TEXT = 'text-emerald-400 [html.light_&]:text-emerald-700';
/**
 * The provider name: 15 px on the phone card, 14 px from sm, at most two
 * lines, never an ellipsis on a real name (see the name block). `overflow-wrap:
 * anywhere` breaks a single long word ("Laadverdien.nl") instead of letting it
 * overflow.
 */
const NAME_CLASS =
  'text-[15px] sm:text-sm font-black text-white leading-[1.125rem] line-clamp-2 [overflow-wrap:anywhere]';
/**
 * The visible net label below xl (decorative twin of the sr-only prefix), in
 * the old card's label idiom: 10 px, uppercase, emerald like the figure
 * (light: emerald-700, ≥ 4.5:1 on the light card). One 12 px line, so the
 * figure (28 px, leading-none) and the label make a 40 px block — the CTA's
 * height beside it on the phone card, the logo's on the tablet card. Over
 * the figure on the phone card (the template reverses the block there, as on
 * the old card: "NETTO PER JAAR*" over "€ 308"), under it on the tablet card.
 * `-mr-[0.05em]` serves the tablet card, where the block is right-aligned:
 * it takes back the tracking Chromium adds after the last glyph, so the
 * asterisk ends on the figure's right edge instead of 0.5 px short of it
 * (measured 2026-10-02). Left-aligned on the phone card it changes nothing
 * anyone sees.
 */
const NET_LABEL_CLASS =
  'block text-[10px] font-bold uppercase tracking-wider leading-3 -mr-[0.05em] text-emerald-300 [html.light_&]:text-emerald-700';
/**
 * The net figure: 28 px below xl — under its label at the left of the phone
 * card's panel, the CTA beside it; beside the name on the tablet card (the
 * old card's 30 px figure was what a visitor looked for first); 18 px in the
 * xl row, whose line is as before (`xl:leading-tight`).
 */
const NET_FIGURE_CLASS =
  'block text-[28px] xl:text-lg font-black leading-none xl:leading-tight tabular-nums';
/**
 * A net figure of seven digits (€ 1.000.000 a year or more — a fleet's volume,
 * typed into the kWh field, which takes up to 10 million kWh) is 168 px at 28 px:
 * wider than the 145 px the phone card's panel leaves it beside "Naar website" at
 * 360 px, where it ran 12 px under the CTA. Six digits are 143 px and fit
 * (measured 2026-10-03). The long figure steps down to 22 px on the phone, on the
 * same 28 px line, so the block stays 40 px and the row keeps its height; from sm
 * its track grows with it.
 */
const NET_FIGURE_LONG = 'max-sm:text-[22px] max-sm:leading-7';
/**
 * A fact's value: 14 px, and 13 px below 375 px. At 360 px the panel's three
 * columns are 87–100 px, and at 14 px the widest facts fill theirs to within
 * half a pixel ("Semi-annually" 99.2 px in 99.7, "20% GEMIDDELD" 99.0 in
 * 99.7) — the payout cell truncates, so it must not hang on that; at 13 px
 * every fact keeps ≥ 2.5 px (measured 2026-10-02). The 20 px line stays, so
 * the card is as tall at 360 as at 375.
 */
const FACT_VALUE_SIZE = 'text-sm max-[375px]:text-[13px]/5';
/**
 * The visible label over each fact below xl, where no header line names the
 * columns: decorative (aria-hidden) — the sr-only prefix is what a screen
 * reader hears, as with the net label. One 12 px line; `truncate` is the
 * backstop where a tablet's facts line runs short.
 */
const FACT_LABEL_CLASS =
  'truncate text-[10px] font-bold uppercase tracking-wider text-fg-muted leading-3';

/**
 * The action: a button, not a bar — `w-auto` at every width (until 2026-10-02
 * a 218 × 44 px full-width bar on every phone row). 40 px in the first row of
 * the phone card's panel, beside the net block, whose 40 px (label + figure)
 * it shares top and bottom — the old card's payout-and-CTA row (it sat in
 * the action strip at 36 px until 2026-10-02, then beside the name); 44 px on
 * line 1 of the tablet card; 36 px in the xl row. Its icon shows at every
 * width: the name no longer shares a line with it (while it did, 2026-10-02,
 * the icon went below 375 px so the name beside it kept its two lines).
 */
const CTA_CLASS =
  'inline-flex w-auto items-center justify-center gap-1.5 h-10 sm:h-11 xl:h-9 px-3 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-black whitespace-nowrap transition-colors shadow-lg shadow-emerald-900/20 group/cta focus-ring';

/**
 * The heading over the panel's fact table: the line DerivedBullets' panel
 * variant sets over its two lists (11 px on the phone, 14 px from md, on a 20 px
 * line beside a 20 px tile), so the three read as one row of headings.
 */
const PANEL_HEADING_CLASS =
  'mb-2 flex items-center gap-2 text-[11px] font-black uppercase leading-5 tracking-[0.2em] text-fg-muted md:mb-2.5 md:text-sm';

/**
 * A link in the panel's foot ("Bekijk profiel", and a form-on provider's "Naar
 * website"): muted text, emerald-700 in the light theme like the net figure.
 */
const PANEL_LINK_CLASS =
  'inline-flex items-center gap-1 whitespace-nowrap text-sm font-bold text-emerald-400 [html.light_&]:text-emerald-700 hover:text-emerald-300 underline underline-offset-2 focus-ring rounded-sm';

/** The 11 px uppercase prefix every fact carries for screen readers. */
const SrLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="sr-only">{children}: </span>
);

export const ResultRow: React.FC<ResultRowProps> = ({
  provider: p,
  index,
  earnings,
  volume,
  erePrice,
  isTopEarner,
  isLowestCommission,
  relative,
  showAudiencePill,
  entranceStep,
  expanded,
  mountDetails,
  detailsEpoch = 0,
  selected,
  selectable,
  ctaHref,
  websiteHref,
  onToggleExpanded,
  onToggleSelected,
  onSignupClick,
  onOpenSignup,
  onWebsiteClick,
  onProfileClick,
  onSmartChargerInfo,
  rowRef,
}) => {
  const { t, language } = useLanguage();
  const terms = useTermsText();
  const location = useLocation();
  const entrance = entranceStep === undefined ? index : entranceStep;

  const profilePath = p.hasProfile && p.slug ? providerProfilePath(language, p.slug) : null;
  // The offer's pricing model (src/lib/offerPricing.js) — a flat row reads as
  // a commission at its `commissionFee`, so everything below is what it was.
  // The % slot shows the model's percentage AT THE COMPARISON'S VOLUME (a
  // staffel's band, a vastrecht offer's nominal %); the tier word reads what
  // the offer really costs there — the effective % the sort and the
  // "laagste commissie" crown compare (commissionSortValue).
  const pricing = rowOfferPricing(p);
  const shownFee = shownCommissionPct(p, volume, erePrice);
  const tier = getCommissionTier(commissionSortValue(p, volume, erePrice));
  // A staffel and a vastrecht offer explain themselves in the icon's title and
  // in the panel's "Verdienmodel" aandachtspunt (src/lib/offerPricingText.ts);
  // never in the row, whose % slot is measured tight below xl.
  const volumeModel = p.deviatingModel === 'tiered' || p.deviatingModel === 'kwh_fee';
  const deviationLabel =
    p.deviatingModel === 'zero_commission'
      ? t('table.deviating_model_zero')
      : p.deviatingModel === 'fixed_rate'
        ? t('table.deviating_model_fixed')
        : p.deviatingModel === 'other'
          ? fillTemplate(t('table.deviating_model_other'), {
              note: p.dossier?.priceModel?.note ?? '',
            })
          : volumeModel
            ? offerPricingTooltip(t, language, pricing)
            : null;
  // The panel has no amber verdienmodel banner (Jasper 2026-10-23: redundant):
  // its first aandachtspunt explains every model (termsVerdict), and the
  // label above stays the row icon's title only.
  // The row prints a tier word under/after the figure on every deviating model
  // but the subscription (which has no commission tier). On the phone card the
  // slot is measured tight: "14% ⓘ GEMIDDELD" ran 2 px into the looptijd
  // column at 360 and 17 px at 320 (a flat "14% GEMIDDELD" keeps 34-53 px), so
  // below sm the icon gives way to the word — the explanation is the panel's
  // (the "Verdienmodel" aandachtspunt). A responsive class,
  // not a width check: the baked HTML is the same at every width.
  const printsTierWord = p.deviatingModel !== 'zero_commission';
  // Subscription model (genuine 0% commission): the pricing slot shows the
  // fixed fee in its native billing period instead of "0%" — the
  // ContractMetrics rule, kept so the two renderers agree.
  const subscriptionFee =
    p.deviatingModel === 'zero_commission' && p.serviceCosts !== null && p.serviceCosts > 0
      ? (p.serviceCostsMonthly ?? p.serviceCosts)
      : null;
  const subscriptionSuffix =
    p.serviceCostsMonthly != null ? t('table.per_month_short') : t('table.per_year_short');
  // The Voorwaarden cell and the Uitbetaling cell, decided by the one
  // presentation module from the ROW's facts: the provider's dossier with the
  // offer's own overrides laid over it (the payload merges them per row), its
  // looptijd and the terms review. A review that is not `read` is one status line.
  const termsCell = termsRowCell({
    dossier: p.dossier,
    contractDuration: p.contractDuration,
    termsReview: p.termsReview,
  });
  const payCell = termsPayoutCell({ dossier: p.dossier, payoutFrequency: p.payoutFrequency });
  // Every panel answers the terms questions (or says they were not checked), so
  // the Details label always promises them.
  const expandLabel = fillTemplate(t('table.show_pros_cons_with_terms'), {
    label: t('table.show_pros_cons'),
  });
  const detailsShort = expanded ? t('table.hide_details') : t('table.details_short');
  const detailsAria = expanded
    ? t('table.hide_details')
    : fillTemplate(t('table.details_aria'), {
        short: t('table.details_short'),
        label: expandLabel,
      });
  const panelId = `provider-details-${p.id}`;

  // The panel's two derived lists and its fact table, from the same facts as the
  // cells above (the row's merged dossier, its looptijd, the review), the
  // comparison's kWh for a minimum, the record's KvK and NEa-list facts, the
  // row's yearly service costs (D28), its pricing model at the comparison's
  // volume (the verdienmodel point that leads the aandachtspunten; a
  // subscription's fee in the period the price slot prints) and the page's
  // language for the redactie's own bullet. The free text `pros` / `cons` of
  // the payload have no reader here any more (D2).
  const verdict = termsVerdict({
    dossier: p.dossier,
    contractDuration: p.contractDuration,
    payoutFrequency: p.payoutFrequency,
    smartChargingRequired: p.smartChargingRequired,
    termsReview: p.termsReview,
    editorialPro: p.editorialPro,
    editorialCon: p.editorialCon,
    annualKwh: volume,
    kvkKnown: p.kvkKnown,
    neaListed: p.neaListed,
    serviceCosts: p.serviceCosts,
    serviceCostsMonthly: p.serviceCostsMonthly,
    pricing,
    // A staffel's ladder emphasises the band the % slot shows at this volume.
    pricingKwh: volume,
    language,
  });
  // What the row is best at beside the others — the lowest commission, the top
  // of the active order (D36) — leads its own pluspunten.
  const proItems = [...relativeBulletItems(relative), ...verdict.pros];
  const factTable = termsFactTable({
    dossier: p.dossier,
    contractDuration: p.contractDuration,
    payoutFrequency: p.payoutFrequency,
    smartChargingRequired: p.smartChargingRequired,
    termsReview: p.termsReview,
    annualKwh: volume,
    neaListed: p.neaListed,
    providerName: p.name,
    // Whose words the facts are, in the one line that says when they were read
    // (termsReviewLine folds it in only after a reading).
    provenance: dossierProvenance(p.dossier),
  });
  // An Inboeker with nothing filed and nothing implied has no row at all — the
  // module's own rule now (termsPresentation inboekerAnswer), so the profile
  // page and the compare pages leave it out as this panel always did.
  const factRows = factTable.rows;

  const translateDuration = (d: string | null): string => {
    if (!d) return UNKNOWN;
    const key = DURATION_LABEL_KEYS[d as CanonicalDuration];
    return key ? t(key) : d;
  };

  // The Uitbetaling cell's words: the frequency ("Per kwartaal") over its
  // timing ("na het jaar"), or the timing alone as the main line when only it
  // is known ("Na het jaar"). The timing carries its tone's sr-only word, as a
  // line of the Voorwaarden cell does; the title says the whole value at once
  // ("Per kwartaal, na het jaar"), for a line the cell cuts.
  const payMain = payCell.main ? terms.text(payCell.main.text) : null;
  const paySub = payCell.sub ? terms.text(payCell.sub.text) : null;
  const payTiming = payCell.sub ?? (payCell.main?.topic === 'payoutTiming' ? payCell.main : null);
  const payWord = payTiming ? terms.toneWord(payTiming.tone) : '';
  const payFull = paySub ? `${payMain}, ${paySub}` : (payMain ?? '');
  const payTitle = payWord ? `${payFull} — ${payWord}` : payFull;

  // The printed chain behind the net figure ("832 ERE × € 0,445 − € 5,55 −
  // € 72,94 = € 291,75", plus the vastrecht and a yearly bound when the model
  // has them): the same cent-exact function as the worked example, worded by
  // src/lib/offerPricingText.ts. Left out when the costs exceed the gross (a
  // low volume, high service costs): the net is floored at € 0, and "… −
  // € 60,00 = € 0,00" would print a sum that does not add up.
  const breakdownTitle = offerBreakdownTitle(
    t,
    language,
    estimateOfferBreakdown(volume, pricing, p.serviceCosts, erePrice)
  );
  // The net figure as printed; seven digits and up take the phone's long-figure size.
  const netText = earnings === null ? null : formatCurrencyEUR(earnings, language, 0);
  const netIsLong = netText !== null && netText.replace(/\D/g, '').length >= 7;

  const showServiceSubline =
    subscriptionFee === null && p.serviceCosts !== null && p.serviceCosts > 0;
  const serviceCostsAmount = showServiceSubline
    ? `${formatCurrencyEUR(p.serviceCosts as number, language, eurDecimals(p.serviceCosts as number))}${t('table.per_year_short')}`
    : '';

  // An unknown fact: the dash for the eye, the words for a screen reader and
  // (as the cell's title) a pointer — "Nog niet bekend", never "niet
  // beschikbaar", which reads as "not offered".
  const unknownLabel = t('table.not_yet_known');
  const unknown = (
    <>
      <span aria-hidden="true">{UNKNOWN}</span>
      <span className="sr-only">{unknownLabel}</span>
    </>
  );

  // Decoration beside the name, which names the row: hidden from assistive
  // technology, or the image's alt or the initials ("MT") would be read before
  // the provider's name. The logo link carries the name itself (aria-label).
  // Always a square: `allowWide={false}` keeps a wordmark inside the chip
  // (smaller) instead of widening it up to 128 px, which pushed the name off
  // the row on every provider with a wide logo. 40 px on the phone card and
  // from xl, 36 px on the tablet card. src/components/LogoPreview.tsx copies
  // these classes for the admin's true-size preview — keep the two in step.
  const logo = (
    <div aria-hidden="true" className="shrink-0">
      <ProviderLogo
        src={p.logoUrl}
        name={p.name}
        onDark={p.logoOnDark}
        fallback="initial"
        allowWide={false}
        loading={index < 3 ? 'eager' : 'lazy'}
        width={40}
        height={40}
        className="w-10 h-10 sm:w-9 sm:h-9 xl:w-10 xl:h-10 aspect-square rounded-xl text-base shrink-0"
        imgClassName="p-1"
      />
    </div>
  );
  const stopAndTrackProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onProfileClick();
  };
  // A provider that takes aanmeldingen through our form keeps the form as its
  // CTA and gets its own HOMEPAGE as a muted second link in the panel's foot
  // (Jasper 2026-10-23: otherwise the site never leads to it) — the engine
  // passes null under unknown data, when there is no form button either. A
  // row without the form has no such link — its CTA IS the website.
  const panelWebsiteHref = p.acceptsSignups ? websiteHref : null;
  const hasFoot = profilePath !== null || panelWebsiteHref !== null;
  const stopAndTrackWebsite = (e: React.MouseEvent) => {
    e.stopPropagation();
    onWebsiteClick();
  };

  // The tags on the top edge, in reading order after the name: the audience
  // (under "Alle" every row has one), then the badges. Each badge carries its
  // rule as its title: no badge without the rule that earned it (plan §12.2).
  const hasTags = showAudiencePill || isTopEarner || isLowestCommission;
  // From sm the group starts above the logo; on a phone at the card's own
  // padding, with the audience as its icon only (the word stays for a screen
  // reader and as the title): all three tags measured 90 + 121 + 116 px, which
  // ran 75 px past a 360 px card and scrolled the page sideways. The max-width
  // keeps any future label from doing that again — a clipped tag, never a
  // sideways page. `top-0` is the padding edge, under the 1 px border: on the
  // cards (below xl) half a pixel up puts the tag's centre on the border's
  // (measured 2026-10-02: 0.5 px low before); the xl row is left as it is.
  const audienceWord = t(p.targetAudience === 'Particulier' ? 'table.personal' : 'table.business');
  const tags = hasTags && (
    <span className="absolute top-0 max-xl:-top-[0.5px] left-3 sm:left-[4.25rem] xl:left-16 -translate-y-1/2 flex items-center gap-1 max-w-[calc(100%-1.5rem)] sm:max-w-[calc(100%-5.25rem)] overflow-hidden">
      {showAudiencePill && (
        <span
          title={audienceWord}
          className={cn(
            TAG_CLASS,
            // Opaque card colour, not the old /10 tint: the tag covers the border.
            'bg-slate-800',
            p.targetAudience === 'Particulier'
              ? 'border-blue-500/40 text-blue-300'
              : 'border-amber-500/40 text-amber-300'
          )}
        >
          {p.targetAudience === 'Particulier' ? (
            <Users className="w-2.5 h-2.5" aria-hidden="true" />
          ) : (
            <Building2 className="w-2.5 h-2.5" aria-hidden="true" />
          )}
          <span className="max-sm:sr-only">{audienceWord}</span>
        </span>
      )}
      {isTopEarner && (
        <span className={BADGE_TAG_CLASS} title={t('table.best_earnings_rule')}>
          {t('table.best_earnings')}
        </span>
      )}
      {isLowestCommission && (
        <span
          // One badge on the phone card: payout outranks commission (the old
          // card's ribbon rule). Hidden there, not left out, so the DOM is
          // the same at every width.
          className={cn(BADGE_TAG_CLASS, isTopEarner && 'max-sm:hidden')}
          title={t('table.best_deal_rule')}
        >
          {t('table.best_deal')}
        </span>
      )}
    </span>
  );

  const cta =
    ctaHref === null ? null : p.acceptsSignups ? (
      <button
        type="button"
        aria-haspopup="dialog"
        // Accessible name must contain the visible label (WCAG 2.5.3).
        aria-label={`${t('signup_form.cta')} ${p.name}`}
        onClick={onOpenSignup}
        // The signup modal's chunk starts loading before the click (lazyModals.ts).
        {...preloadOnIntent(loadSignupModal)}
        className={CTA_CLASS}
      >
        {t('signup_form.cta')}
        <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-0.5 transition-transform" />
      </button>
    ) : (
      <a
        href={ctaHref}
        target="_blank"
        rel="noopener noreferrer"
        // The name starts with the visible label (WCAG 2.5.3): "Naar website Laadbeloning".
        aria-label={`${t('table.website_short')} ${p.name}`}
        onClick={onSignupClick}
        className={CTA_CLASS}
      >
        {t('table.website_short')}
        <ExternalLink className="w-4 h-4 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5 transition-transform" />
      </a>
    );

  // The panel body. Plain <li>, not m.li: an entrance stagger would bake
  // `style="opacity:0"` onto every bullet in the static HTML — the hidden-
  // content pattern src/__tests__/prerenderBody.test.tsx exists to catch.
  // On the phone card the panel opens under the action strip, which closes the
  // collapsed card flush with its bottom edge — so the open panel brings its
  // own bottom padding (max-sm:pb-3).
  const panel = (
    <div className="pt-4 mt-3 border-t border-slate-700/50 max-sm:pb-3">
      {/* Composition A (D20). xl: Voordelen 3/12 · Aandachtspunten 3/12 · the fact
          table 6/12 across two rows, the profile link in the second row under the
          two lists, at the foot. md to xl: the two lists stacked beside the table,
          the profile link across the foot. Below md one column in reading order.
          The blocks place themselves by their own classes; the DOM order (pros,
          cons, table, foot) is the reading order at every width. */}
      <div
        className={cn(
          'grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-x-8 xl:grid-cols-[3fr_3fr_6fr] xl:grid-rows-[auto_1fr]',
          // The foot is a row of its own from md. A provider without a profile page (and
          // without the form's website link) has none, and an empty third track would
          // still leave its 16 px gap behind.
          hasFoot ? 'md:grid-rows-[auto_1fr_auto]' : 'md:grid-rows-[auto_1fr]'
        )}
      >
        <DerivedBullets
          pros={proItems}
          cons={verdict.cons}
          side="pros"
          variant="panel"
          linkSources
          headingLevel={4}
          keyPrefix={`row-${p.id}`}
          className="md:col-start-1 md:row-start-1"
        />
        <DerivedBullets
          pros={proItems}
          cons={verdict.cons}
          side="cons"
          variant="panel"
          linkSources
          headingLevel={4}
          keyPrefix={`row-${p.id}`}
          className="md:col-start-1 md:row-start-2 xl:col-start-2 xl:row-start-1"
        />
        <div className="min-w-0 md:col-start-2 md:row-start-1 md:row-end-3 xl:col-start-3">
          <h4 className={PANEL_HEADING_CLASS}>
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-slate-700/40">
              <ListChecks className="h-3 w-3 text-fg-muted" aria-hidden="true" />
            </span>
            {t('table.col_terms')}
          </h4>
          {/* From xl the table's rows touch: their 20 px lines carry the rhythm. Chosen
              against a 335 px open row at 1280: seven questions and two closing lines
              measured 345 with the 2 px between the rows, 333 without them. (The legend
              line, about 24 px, has been gone since D26; not re-measured.) */}
          <TermsFactTable
            rows={factRows}
            review={factTable.review}
            providerName={p.name}
            variant="panel"
            linkSources={false}
            onSmartInfo={onSmartChargerInfo}
            keyPrefix={`row-${p.id}`}
            className="xl:[&_dl]:gap-y-0"
          />
        </div>
        {/* The way to the profile: at the right of the foot from sm (D27, Jasper
            2026-10-08). The service costs are on the collapsed row already, so the
            panel ends with its three columns and this link — and, for a provider
            that takes aanmeldingen through our form, its website before it, as
            muted as the profile link (2026-10-23): the form stays the row's CTA. */}
        {hasFoot && (
          <div className="flex flex-wrap gap-x-5 gap-y-2 sm:justify-end md:col-start-1 md:col-end-3 md:row-start-3 xl:row-start-2 xl:self-end">
            {panelWebsiteHref !== null && (
              <a
                href={panelWebsiteHref}
                target="_blank"
                rel="noopener noreferrer"
                // The name starts with the visible label (WCAG 2.5.3), as the CTA's does.
                aria-label={`${t('table.website_short')} ${p.name}`}
                onClick={stopAndTrackWebsite}
                className={PANEL_LINK_CLASS}
              >
                {t('table.website_short')}
                <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
              </a>
            )}
            {profilePath && (
              <Link
                to={profilePath}
                state={providerBackState(location)}
                onClick={stopAndTrackProfile}
                className={PANEL_LINK_CLASS}
              >
                {t('table.view_profile')}
                <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <li
      id={`result-${p.id}`}
      ref={rowRef}
      tabIndex={-1}
      // CSS entrance (reveal-rise), capped stagger — never a motion `initial`,
      // which would bake opacity:0 into the prerendered HTML. Only while the
      // engine says this row is entering (see `entranceStep`).
      style={
        entrance === null
          ? undefined
          : { animationDelay: `${Math.min(entrance, ENTRANCE_MAX_STEPS) * ENTRANCE_STAGGER_MS}ms` }
      }
      // No bottom padding on the phone card: its action strip is the card's
      // bottom edge (index.css draws the strip's hairline and fade).
      className={cn(
        entrance !== null && 'reveal-rise',
        'result-grid relative rounded-xl xl:rounded-2xl border bg-slate-800 px-3 pt-3 sm:pb-3 xl:px-4 xl:py-2.5 transition-colors duration-300 focus-ring cursor-pointer',
        expanded
          ? 'border-emerald-500/40 bg-slate-800/90 shadow-2xl shadow-emerald-900/10'
          : isTopEarner
            ? 'border-emerald-500/60'
            : 'border-slate-700/60 hover:border-slate-600'
      )}
      // Pointer-only expand convenience — the accessible control is the
      // Details button (aria-expanded/controls). Anything interactive inside
      // the row keeps its own click.
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('a, button, input, label')) return;
        onToggleExpanded();
      }}
    >
      {/* ── select ── On the phone card it opens the action strip, with the
          word beside the box; from sm the bare box leads line 1 in its 44 px
          (xl: 36 px) hit box, as before. */}
      <div className="rg-select flex items-center">
        <label className="flex h-11 items-center gap-1.5 cursor-pointer sm:w-11 sm:-m-2 sm:justify-center xl:w-9 xl:h-9 xl:-m-1.5">
          <input
            type="checkbox"
            checked={selected}
            disabled={!selected && !selectable}
            onChange={onToggleSelected}
            aria-label={fillTemplate(t('table.compare_checkbox_aria'), { name: p.name })}
            title={!selected && !selectable ? t('table.compare_limit_hint') : undefined}
            className="w-5 h-5 shrink-0 rounded border-slate-600 bg-slate-900 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 focus-ring"
          />
          {/* Decorative: the box's own name ("Vergelijk Stekker") starts with
              this word, so a screen reader hears it once, with the box. It sits
              inside the label, so tapping the word ticks the box. */}
          <span
            aria-hidden="true"
            className="sm:hidden text-[11px] font-bold text-slate-300 whitespace-nowrap"
          >
            {t('table.col_compare')}
          </span>
        </label>
      </div>

      {/* ── provider ── */}
      <div className="rg-name min-w-0 flex items-center gap-2 sm:gap-2.5">
        {profilePath ? (
          <Link
            to={profilePath}
            state={providerBackState(location)}
            onClick={stopAndTrackProfile}
            aria-label={p.name}
            className="shrink-0 rounded-xl transition-opacity hover:opacity-80 focus-ring"
          >
            {logo}
          </Link>
        ) : (
          logo
        )}
        {/* A fixed height, so every row is as tall as the next: room for a
            two-line name (36 px); a shorter name is centred in it. The name
            wraps instead of truncating. On the phone card it shares the
            header only with the logo and the 40 px profile arrow: its column
            is 206 px at 360 px, 221 at 375 and 258 at 412, in both languages
            (measured 2026-10-03; 139 / 132 px in Dutch at 360 / 375 while
            the CTA stood beside it) — the longest name on prd ("Renewable
            Energy Solutions") takes two lines at 15 px at every phone width,
            and at 14 px in the ~138 px xl column. */}
        <div className="min-w-0 flex flex-col justify-center h-10">
          <SrLabel>{t('table.provider')}</SrLabel>
          {profilePath ? (
            <Link
              to={profilePath}
              state={providerBackState(location)}
              onClick={stopAndTrackProfile}
              className="block min-w-0 hover:text-emerald-400 focus:text-emerald-400 focus-ring rounded-sm transition-colors"
            >
              <h3 className={NAME_CLASS} title={p.name}>
                {p.name}
              </h3>
            </Link>
          ) : (
            <h3 className={NAME_CLASS} title={p.name}>
              {p.name}
            </h3>
          )}
          {tags}
        </div>
        {/* The old card's round arrow to the profile, at the right end of the
            phone card's header: 40 px, the logo's box at the other end of the
            line (both centred in it, so they share top and bottom), its
            right edge on the content's — the panel box's edge and the
            Details chevron's under it. Phone only: from sm the logo and the
            name link (they link here too) and the panel's "Bekijk profiel"
            do the job, as before. Its name says where it goes and for whom
            ("Bekijk profiel Stekker"), unlike the logo and name links, which
            are named by the provider alone. Light theme: the border and the
            icon ride the slate remap like the card's own border (measured,
            plan §10). */}
        {profilePath && (
          <Link
            to={profilePath}
            state={providerBackState(location)}
            onClick={stopAndTrackProfile}
            aria-label={`${t('table.view_profile')} ${p.name}`}
            className="sm:hidden ml-auto shrink-0 inline-flex w-10 h-10 items-center justify-center rounded-full border border-slate-700/60 text-slate-300 hover:text-emerald-300 hover:border-emerald-500/50 active:bg-slate-700/40 transition-colors focus-ring"
          >
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        )}
      </div>

      {/* ── net per year ── */}
      {/* At the left of the phone card's panel, its label over the figure
          (the template reverses the block there and insets it as the panel's
          content: 10 px in, 8 down), the CTA beside it. It holds the flexible
          track, so no fit-content minimum or nowrap is needed: the figure is
          a block, one line anyway. Right-aligned at the end of line 1 on the
          tablet card, the label under the figure; left in its column from xl. */}
      <div className="rg-net text-left sm:text-right xl:text-left min-w-0">
        <SrLabel>{t('table.net_per_year_short')}</SrLabel>
        {/* 28 px on the phone and tablet cards, the figure a visitor looks
            for first as on the old card (22 px on a phone when it runs to
            seven digits, NET_FIGURE_LONG); 18 px in the xl row. */}
        {earnings === null ? (
          <span className={cn(NET_FIGURE_CLASS, 'text-fg-muted')} title={unknownLabel}>
            {unknown}
          </span>
        ) : (
          <span
            className={cn(NET_FIGURE_CLASS, EMERALD_TEXT, netIsLong && NET_FIGURE_LONG)}
            title={breakdownTitle}
          >
            {netText}
          </span>
        )}
        {/* A visible label below xl, where no header names the column — the
            full words on the phone card too (the compact "netto/jr*" went
            with the three-line row, 2026-10-02). Decorative — the sr-only
            prefix above is the one the screen reader hears. */}
        <span aria-hidden="true" className={cn(NET_LABEL_CLASS, 'xl:hidden')}>
          {t('table.net_per_year_short')}
        </span>
        {showServiceSubline && (
          <span className="hidden xl:block text-[10px] font-semibold text-amber-300 leading-tight whitespace-nowrap">
            {fillTemplate(t('table.service_costs_inline'), { amount: serviceCostsAmount })}
          </span>
        )}
      </div>

      {/* ── action ── At the right of the phone card's panel, beside the net
          block — the old card's payout-and-CTA row (the template insets it
          10 px from the box's edge, 8 down); at the right end of line 1 on
          the tablet card, after the net figure; its own column from xl. */}
      <div className="rg-cta flex items-center justify-end xl:justify-start">{cta}</div>

      {/* ── facts: labelled cells in a panel below xl, five cells from xl ── */}
      {/* Below xl the old card's stats panel (Jasper 2026-10-02): every fact
          under a visible label — below lg commission · looptijd · uitbetaling,
          then the voorwaarden line under a hairline on the same three columns
          (the phone card, and the tablet card up to 1024 px, where a line of
          five with the two terms values does not fit); from lg to xl one line
          of five (plan-voorwaarden-vergelijking-2026-10-07 §6.1: at 768 px it
          cut five strings in the mockup). On the tablet card this wrapper IS
          the panel (the sm: utilities):
          10 px of air on every side, the panel's inner rhythm (index.css),
          8 px at the top, where the labels' line holds 2.4 px above their
          capitals. On the phone card it is the lower part of a bigger one
          (2026-10-03): the row's ::after draws one box around the net + CTA
          row and these facts (index.css), so the wrapper paints no box of its
          own there — only its top border, the hairline under the net + CTA
          row (max-sm:, at the cells' inset like the terms line's); the
          template sets its margins and padding. From xl the wrapper is
          display:contents (index.css): its panel classes paint nothing and
          the children are the row's cells. No `flex`/`grid` utility here on
          purpose — the display lives in the CSS template, where the
          components layer cannot override it. */}
      <div className="rg-facts min-w-0 overflow-hidden sm:rounded-lg sm:border sm:border-slate-700/30 sm:bg-slate-900/50 sm:px-2.5 sm:pt-2 sm:pb-2.5 max-sm:border-t max-sm:border-slate-700/40 text-[11px] text-slate-300">
        <div className="rg-fee min-w-0">
          <SrLabel>{t('table.fee')}</SrLabel>
          {/* A subscription has no commission: its slot is labelled for what it shows. */}
          <span aria-hidden="true" className={cn(FACT_LABEL_CLASS, 'block xl:hidden')}>
            {subscriptionFee !== null ? t('table.fixed_costs') : t('table.fee')}
          </span>
          {p.commissionFee === null ? (
            <span
              className="font-black text-fg-muted max-xl:text-sm"
              title={t('table.no_commission')}
            >
              {UNKNOWN}
              <span className="sr-only">{t('table.no_commission')}</span>
            </span>
          ) : subscriptionFee !== null ? (
            <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
              <span className="sr-only">{t('table.fixed_costs')} </span>
              <span className={cn(FACT_VALUE_SIZE, 'font-black text-white')}>
                {formatCurrencyEUR(subscriptionFee, language, eurDecimals(subscriptionFee))}
              </span>
              <span className="text-[10px] font-bold text-fg-muted">{subscriptionSuffix}</span>
              {/* The title explains the icon to a pointer; the aria-label to a screen reader. */}
              <span className="inline-flex self-center" title={deviationLabel ?? undefined}>
                <AlertCircle
                  className="w-3.5 h-3.5 text-amber-400"
                  role="img"
                  aria-label={deviationLabel ?? ''}
                />
              </span>
            </span>
          ) : (
            // One line below xl ("13% laag", or "15% + € 9,99/jr"): every
            // row's value line is as tall as the next. From xl a column: the
            // tier word drops under the figure, so "19% gemiddeld" + an icon
            // never runs into the looptijd cell beside it (measured at 1024
            // and 1440).
            <span className="inline-flex items-baseline gap-1 whitespace-nowrap xl:flex-col xl:items-start xl:gap-0 xl:leading-tight">
              <span className="inline-flex items-baseline gap-1">
                <span className={cn(FACT_VALUE_SIZE, 'xl:text-base font-black text-white')}>
                  {formatCommissionPct(shownFee ?? p.commissionFee, language)}%
                </span>
                {deviationLabel && (
                  <span
                    className={cn(
                      'self-center',
                      printsTierWord ? 'hidden sm:inline-flex' : 'inline-flex'
                    )}
                    title={deviationLabel}
                  >
                    <AlertCircle
                      className="w-3.5 h-3.5 text-amber-400"
                      role="img"
                      aria-label={deviationLabel}
                    />
                  </span>
                )}
              </span>
              {printsTierWord && (
                <span
                  className={cn(
                    // tracking-wide on the cards below xl: "20% GEMIDDELD" is
                    // then 99 px (101 with tracking-wider) — it fits the phone
                    // panel's commission column at 375 px with 6 px to spare
                    // and the tablet's at 768 with 15 (measured 2026-10-02).
                    // The widest in the feed, "17,5% GEMIDDELD" (107 px, the
                    // business view only), runs 4.5 px into the 10 px gap at
                    // 360 and 1.8 px at 375.
                    'text-[10px] font-bold uppercase tracking-wider max-xl:tracking-wide xl:block',
                    // Below xl the service costs take the tier word's place, so
                    // the value stays one line; from xl it is back under the
                    // figure and the costs sit under the net figure.
                    showServiceSubline && 'hidden',
                    COMMISSION_TIER_TEXT_CLASS[tier]
                  )}
                >
                  {t(commissionTierLabelKey(tier))}
                </span>
              )}
              {/* Below xl the service costs ride the fee ("15% + € 9,99/jr"):
                  the net cell has no room for the full sub-line below xl. */}
              {showServiceSubline && (
                <span
                  // Full amber, not /90: on the light card the 90 % tint measured
                  // 3.9:1 at 10 px; the solid #b45309 gives 4.6:1.
                  className="xl:hidden text-[10px] font-semibold text-amber-300"
                  title={fillTemplate(t('table.service_costs_inline'), {
                    amount: serviceCostsAmount,
                  })}
                >
                  <span className="sr-only">{t('table.service_costs')} </span>
                  {fillTemplate(t('table.service_costs_compact'), { amount: serviceCostsAmount })}
                </span>
              )}
            </span>
          )}
        </div>
        <div className="rg-dur min-w-0 whitespace-nowrap">
          <SrLabel>{t('table.duration')}</SrLabel>
          <span aria-hidden="true" className={cn(FACT_LABEL_CLASS, 'block xl:hidden')}>
            {t('table.duration')}
          </span>
          <span
            className={cn(
              FACT_VALUE_SIZE,
              'font-bold',
              p.contractDuration ? 'text-white' : 'text-fg-muted'
            )}
            title={p.contractDuration ? undefined : unknownLabel}
          >
            {p.contractDuration ? translateDuration(p.contractDuration) : unknown}
          </span>
        </div>
        {/* The frequency over its timing (§6.1): 14 px over a 10 px line below
            xl (13 px below 375 px), 12 over 10 in the xl row. The timing line
            is reserved on EVERY row — an empty 12 px line where the timing is
            not one the row can say — so every row is as tall as the next and
            the frequency stands at the same height in each. When only the
            timing is known it is the main line ("Na het jaar"): a dash over a
            sub-line read as broken in the mockup. Block lines, each `truncate`
            (the backstop for "Semi-annually" where the line is short), the
            whole value in the title. */}
        <div className="rg-pay min-w-0">
          <SrLabel>{t('table.payout')}</SrLabel>
          <span aria-hidden="true" className={cn(FACT_LABEL_CLASS, 'block xl:hidden')}>
            {t('table.payout_frequency_short')}
          </span>
          {payCell.main ? (
            <span
              className={cn(
                FACT_VALUE_SIZE,
                'block truncate font-bold xl:text-xs',
                TONE_TEXT[payCell.main.tone]
              )}
              title={payTitle}
            >
              {payMain}
              {!paySub && payWord && <span className="sr-only">{` (${payWord})`}</span>}
            </span>
          ) : (
            <span
              className={cn(FACT_VALUE_SIZE, 'block font-bold xl:text-xs text-fg-muted')}
              title={unknownLabel}
            >
              {unknown}
            </span>
          )}
          {paySub && payCell.sub ? (
            <span
              className={cn(
                'block h-3 truncate text-[10px] font-semibold leading-3',
                TONE_TEXT[payCell.sub.tone]
              )}
            >
              <span className="sr-only">, </span>
              {paySub}
              {payWord && <span className="sr-only">{` (${payWord})`}</span>}
            </span>
          ) : (
            <span aria-hidden="true" className="block h-3" />
          )}
        </div>
        {/* The terms as two values (D5, D19): the cancel deadline over the
            term, in their tones, or ONE status line when the terms were not
            read (TermsCell; the words and tones come from termsPresentation).
            Below lg (the phone panel and the tablet card up to 1024 px) the
            label and the values open the panel's second line, under its
            hairline, on the panel's own columns — the label (`rg-tl`) under
            COMMISSIE with the smart mark under it, the values (`rg-tv`) over
            the LOOPTIJD and UITBETALING columns, two lines reserved; in the
            tablet's line of five (lg to xl) the label stands over them; from
            xl the header names the column. Its display (grid / flex column)
            and the placements live in the template. The hairline sits
            optically midway: 6 px under the values' line (whose last 5 px are
            air under the baseline), 10 px over the label and the values.
            `max-lg:justify-start`: below lg the values' box is two lines tall
            even when it holds one status line, which then stays on the label's
            line (the cell's own `justify-center` is a utility, out of reach of
            the template's layer). */}
        <div className="rg-terms min-w-0 max-lg:mt-1.5 max-lg:pt-2.5 max-lg:border-t max-lg:border-slate-700/40">
          <span aria-hidden="true" className={cn(FACT_LABEL_CLASS, 'rg-tl block xl:hidden')}>
            {t('table.col_terms')}
          </span>
          <TermsCell cell={termsCell} className="rg-tv max-lg:justify-start" />
        </div>
        {/* The smart-charger mark, flipped (D3, D22): a required smart charger
            is the norm and prints NOTHING, so this cell stays empty; not
            required is the green "Ook zonder slimme laadpaal", unknown a muted
            "Niet gecontroleerd" — never a warning. Below xl, where no header
            names the column, the card's mark, which names itself ("Slimme
            lader: niet gecontroleerd": a bare "Niet gecontroleerd" under the
            VOORWAARDEN label read as "terms not checked" in the mockup) — under
            the label below lg, in the unlabelled fifth cell from lg; from xl
            the row's mark under the column's word. Two marks, one shown per
            width: the DOM is the same at every width. No `whitespace-nowrap`
            here: the mark sets its words on two lines. */}
        <div className="rg-smart min-w-0">
          <SmartMark required={p.smartChargingRequired} variant="card" className="xl:hidden" />
          <SmartMark required={p.smartChargingRequired} className="hidden xl:flex" />
        </div>
      </div>

      {/* ── details toggle ── At the right end of the phone card's action
          strip, its chevron on the content's right edge, under the panel
          box's and the profile arrow's (Vergelijk opens the strip, Details
          closes it); at the right of line 2 on the tablet card, its own last
          cell from xl. */}
      <div className="rg-details flex items-center justify-end">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
          aria-expanded={expanded}
          // The panel exists while collapsed on Dutch pages only; on /en it is
          // mounted on expand, and a reference to a missing id is no reference.
          aria-controls={mountDetails || expanded ? panelId : undefined}
          aria-label={detailsAria}
          className={cn(
            'inline-flex shrink-0 items-center justify-center gap-1 h-11 min-w-11 xl:h-9 xl:min-w-9 px-2 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors focus-ring',
            // Tighter in the phone strip (no gap before the chevron: its icon
            // box carries 4 px of air): "Inklappen" open takes 91 px instead
            // of 106. `-mr-1` takes back the button's own 4 px of right
            // padding, so the chevron's box ends on the content's right edge,
            // the panel box's and the profile arrow's above it; the button
            // grows to the left when the word turns into "Inklappen".
            // shrink-0: the word is never squeezed.
            // In the strip the button is 36 px tall (the open tint is a pill
            // of that height, as in the chosen mockup), and a transparent
            // ::before 4 px above and below keeps the 44 px tap target over
            // the whole strip.
            'max-sm:-mr-1 max-sm:px-1 max-sm:gap-0 max-sm:tracking-wide max-sm:h-9 max-sm:relative max-sm:before:absolute max-sm:before:inset-x-0 max-sm:before:-inset-y-1',
            // Light: the literal emerald-800 hex — the theme remaps the
            // emerald-800 token to a pastel (index.css), which would vanish.
            expanded
              ? 'text-emerald-400 [html.light_&]:text-[#065f46] bg-emerald-500/10'
              : 'text-fg-muted hover:text-slate-200 hover:bg-slate-700/60'
          )}
        >
          {/* Open, the word gives way to the chevron from xl: "Inklappen" + a
              chevron needs ~110 px and the column is 5.5rem, so the chevron was
              the one squeezed to nothing. The name stays "Inklappen" (aria-label). */}
          <span className={expanded ? 'xl:hidden' : undefined}>{detailsShort}</span>
          <ChevronDown
            className={cn(
              'w-4 h-4 shrink-0 transition-transform duration-200 motion-reduce:transition-none',
              expanded && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* ── details panel ── On Dutch pages mounted while collapsed (height 0
          + inert) so the baked HTML carries the verdict; on /en mounted on
          expand, because most English bullets are still Dutch fallbacks.
          A panel that is open when it mounts — a row restored open on Back,
          or remounted by `detailsEpoch` — renders open at once in both
          languages (`initial={false}`, on the panel and on the presence);
          only a click animates. */}
      <div className="rg-panel min-w-0">
        {mountDetails ? (
          <m.div
            key={detailsEpoch}
            id={panelId}
            initial={false}
            animate={expanded ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
            inert={!expanded}
          >
            {panel}
          </m.div>
        ) : (
          <AnimatePresence key={detailsEpoch} initial={false}>
            {expanded && (
              <m.div
                id={panelId}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                {panel}
              </m.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </li>
  );
};
